/**
 * UX Audit Phase 2 (Task #2050) — Credentials route slice.
 *
 * Covers: credentials list, issuance, verify, revocation, templates,
 * marketplace, bundles, audit trail, issuer portal.
 */
import { lazy } from "react";
import { Route, Redirect } from "wouter";
import {
  ProtectedRoute,
  FeatureGuardedRoute,
} from "@/components/routing/RouteGate";
import { AliasRedirect } from "@/components/routing/AliasRedirect";
import type { Permission } from "@/lib/auth-context";

const Credentials = lazy(() => import("@/pages/credentials/Credentials"));
const CredentialIssuance = lazy(() => import("@/pages/credentials/CredentialIssuance"));
const CredentialDetail = lazy(() => import("@/pages/credentials/CredentialDetail"));
const RevocationHub = lazy(() => import("@/pages/credentials/RevocationHub"));
const RevocationWizard = lazy(() => import("@/pages/credentials/RevocationWizard"));
const RevocationGovernance = lazy(() => import("@/pages/credentials/RevocationGovernance"));
// RevocationCenter is embedded as the "center" tab inside RevocationHub.
// The legacy public route /revocation-center is already redirected to
// /revocation?tab=center via legacyRedirects.tsx.  No standalone route is
// needed here.  (Task #2051 — revocation consolidation)
const VerifyAndValidate = lazy(() => import("@/pages/credentials/VerifyAndValidate"));
const TemplatesAndIssuers = lazy(() => import("@/pages/credentials/TemplatesAndIssuers"));
const CredentialTemplates = lazy(() => import("@/pages/credentials/CredentialTemplates"));
const CredentialTemplateDetail = lazy(() => import("@/pages/credentials/CredentialTemplateDetail"));
const TemplateMarketplace = lazy(() => import("@/pages/credentials/TemplateMarketplace"));
const BundleConfiguration = lazy(() => import("@/pages/credentials/BundleConfiguration"));
const AuditTrail = lazy(() => import("@/pages/credentials/AuditTrail"));
const IssuerPortal = lazy(() => import("@/pages/credentials/IssuerPortal"));
const ExpiringCredentials = lazy(() => import("@/pages/credentials/ExpiringCredentials"));
const Evidence = lazy(() => import("@/pages/credentials/Evidence"));

export function credentialRoutes() {
  return (
    <>
      <Route path="/expiry">{() => <FeatureGuardedRoute feature="compliance.credentials" permission="credential.view" component={ExpiringCredentials} />}</Route>
      <Route path="/credentials">{() => <FeatureGuardedRoute feature="compliance.credentials" permission="credential.view" component={Credentials} />}</Route>
      <Route path="/credentials/issue">{() => <FeatureGuardedRoute feature="compliance.credentialIssuance" permission="credential.issue" component={CredentialIssuance} />}</Route>
      <Route path="/credentials/verify">{() => <FeatureGuardedRoute feature="compliance.credentials" permission="credential.view" component={VerifyAndValidate} />}</Route>
      <Route path="/credentials/bulk-revoke">{() => <AliasRedirect from="/credentials/bulk-revoke" to="/revocation?tab=center" />}</Route>
      <Route path="/credentials/batch-validate">{() => <AliasRedirect from="/credentials/batch-validate" to="/credentials/verify?mode=batch" />}</Route>
      <Route path="/credentials/kiosk">{() => <AliasRedirect from="/credentials/kiosk" to="/credentials/verify?mode=kiosk" />}</Route>
      <Route path="/credentials/:id">{() => <FeatureGuardedRoute feature="compliance.credentials" permission="credential.view" component={CredentialDetail} />}</Route>
      <Route path="/issuer-portal">{() => <FeatureGuardedRoute feature="compliance.credentials" permission="credential.view" component={IssuerPortal} />}</Route>
      <Route path="/bundles">{() => <FeatureGuardedRoute feature="compliance.credentialBundles" permission="credential.manage" component={BundleConfiguration} />}</Route>
      <Route path="/credential-audit-trail">{() => <FeatureGuardedRoute feature="compliance.credentials" permission="credential.manage" component={AuditTrail} />}</Route>
      <Route path="/revocation">{() => <FeatureGuardedRoute feature="compliance.credentials" permission={["credential.revoke", "settings.manage"] as Permission[]} component={RevocationHub} />}</Route>
      <Route path="/credential-templates/:id">{() => <FeatureGuardedRoute feature="compliance.credentials" permission="credential.view" component={CredentialTemplateDetail} />}</Route>
      <Route path="/credential-templates">{() => <FeatureGuardedRoute feature="compliance.credentials" permission="credential.view" component={TemplatesAndIssuers} />}</Route>
      <Route path="/template-marketplace">{() => <ProtectedRoute permission="credential.view" component={TemplateMarketplace} />}</Route>
      <Route path="/evidence">{() => <ProtectedRoute permission="evidence.view" component={Evidence} />}</Route>
    </>
  );
}
